import { useI18n } from '@ecom/i18n';
import { Button, Field, Input } from '@ecom/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactElement, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { useResetPasswordMutation } from '../api';
import { AuthLayout } from '../components/AuthLayout';
import { StatusIcon } from '../components/StatusIcon';
import { translateFieldError } from '../field-error';
import { resetPasswordFormSchema, type ResetPasswordFormValues } from '../schemas';
import { useUrlToken } from '../use-url-token';

import { ApiError } from '@/lib/api-client';
import { whenDefined } from '@/lib/optional-props';

export function ResetPasswordPage(): ReactElement {
  const { t } = useI18n();
  // The emailed link carries the token in the URL FRAGMENT (`#token=…`); links sent before that change used `?token=…`
  // and still work. It is read when the page opens (and when a new link is pasted into this same tab), then removed from
  // the address bar so it does not linger in history or a screenshot.
  const token = useUrlToken();
  const resetMutation = useResetPasswordMutation();
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({ resolver: zodResolver(resetPasswordFormSchema) });

  async function onSubmit(values: ResetPasswordFormValues): Promise<void> {
    if (token === null) return;
    setFormError(null);
    try {
      await resetMutation.mutateAsync({ token, password: values.password });
      setDone(true);
    } catch (error) {
      // The caller holds a secret token, so telling « invalid / expired » apart from a fault leaks nothing
      // (auth.controller.ts) — but the sentence is OURS, translated, never the server's text.
      if (error instanceof ApiError && error.status === 429) setFormError(t('errors.rateLimited'));
      else if (error instanceof ApiError && error.status < 500)
        setFormError(t('auth.reset.failed'));
      else setFormError(t('errors.network'));
    }
  }

  if (token === null) {
    return (
      <AuthLayout
        icon={<StatusIcon kind="link" />}
        title={t('auth.reset.invalidTitle')}
        subtitle={t('auth.reset.invalidSubtitle')}
        footer={
          <Link to="/forgot-password" className="font-medium text-accent hover:underline">
            {t('auth.reset.requestNew')}
          </Link>
        }
      >
        <p className="text-sm text-inkFaint">{t('auth.reset.invalidHint')}</p>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout
        icon={<StatusIcon kind="check" />}
        title={t('auth.reset.doneTitle')}
        subtitle={t('auth.reset.doneSubtitle')}
        footer={
          <Link to="/login" className="font-medium text-accent hover:underline">
            {t('auth.reset.signIn')}
          </Link>
        }
      >
        <p className="text-sm text-inkFaint">{t('auth.reset.doneHint')}</p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.reset.title')}
      subtitle={t('auth.reset.subtitle')}
      footer={
        <Link to="/login" className="font-medium text-accent hover:underline">
          {t('auth.forgot.back')}
        </Link>
      }
    >
      <form
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
        noValidate
        className="flex flex-col gap-4"
      >
        <Field
          label={t('auth.reset.newPassword')}
          {...whenDefined('error', translateFieldError(t, errors.password?.message))}
        >
          {(bind) => (
            <Input
              {...bind}
              {...register('password')}
              type="password"
              autoComplete="new-password"
              autoFocus
            />
          )}
        </Field>

        <Field
          label={t('auth.confirmPassword.label')}
          {...whenDefined('error', translateFieldError(t, errors.confirmPassword?.message))}
        >
          {(bind) => (
            <Input
              {...bind}
              {...register('confirmPassword')}
              type="password"
              autoComplete="new-password"
            />
          )}
        </Field>

        {formError !== null ? (
          <div
            role="alert"
            className="flex flex-col gap-2 rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical"
          >
            <p>{formError}</p>
            <Link to="/forgot-password" className="font-medium underline">
              {t('auth.reset.requestNew')}
            </Link>
          </div>
        ) : null}

        <Button type="submit" loading={resetMutation.isPending} className="mt-1 w-full">
          {t('auth.reset.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}
